import { ApiError, uuid } from "@gjwtrack/client";
import type { AdminOverview, AdminProject } from "@gjwtrack/domain";
import { ChevronRight, MoreHorizontal, Plus, Search, Users, X } from "lucide-react";
import { Fragment, useState } from "react";
import { Link } from "react-router";
import { client } from "../../lib/client.ts";
import { Menu } from "../../ui/Menu.tsx";
import { Button, Dialog, DialogContent, DialogHeader, Input, InputSize, Panel, Select, SelectType, type SelectItem } from "../../ui/ring.ts";
import { SimpleSelect } from "../../ui/SimpleSelect.tsx";
import { SettingsTitle } from "./ProjectSettings.tsx";
import { groupLabel, PersonAvatar, roleLabel, Td, Th, userOf, type Run } from "./shared.tsx";

/**
 * YouTrack's project People page. GTrack has no team group of its own, so the
 * "Project Team" is simply everyone holding a role grant in this project;
 * "Other People with Access" is everyone reaching it through a global grant or
 * through the administrator flag. Adding people and changing their roles is
 * grantRole / revokeRole — there is no separate membership table.
 *
 * Not implemented: the header checkbox with a bulk Remove, and YouTrack's
 * "Add to team" switch (there is no team to add to).
 */
export function ProjectPeople({ p, data, run, reload }: { p: AdminProject; data: AdminOverview; run: Run; reload: () => Promise<void> }) {
  const [q, setQ] = useState("");
  const [adding, setAdding] = useState(false);
  const [open, setOpen] = useState<Record<string, boolean>>({});

  const team = groupGrants(data.grants.filter((g) => g.projectId === p.id));
  const global = groupGrants(data.grants.filter((g) => g.projectId === null));
  const admins = data.users.filter((u) => u.isAdmin && !global.some((r) => r.kind === "user" && r.id === u.id));

  const match = (text: string): boolean => !q.trim() || text.toLowerCase().includes(q.trim().toLowerCase());
  const rowText = (r: Grantee): string =>
    r.kind === "group" ? groupLabel(data, r.id) : `${userOf(data, r.id)?.name ?? ""} ${userOf(data, r.id)?.email ?? ""}`;

  /** A role dropdown's new selection, turned into the grants and revocations that get there. */
  const setRoles = async (row: Row, next: number[]): Promise<void> => {
    for (const roleId of next.filter((id) => !row.roles.some((x) => x.roleId === id))) {
      if (!(await run("grantRole", { projectId: p.id, ...granteeInput(row), role: roleId }))) return;
    }
    for (const g of row.roles.filter((x) => !next.includes(x.roleId))) {
      if (!(await run("revokeRole", { grantId: g.grantId }))) return;
    }
  };
  const removeAll = async (row: Row): Promise<void> => {
    for (const g of row.roles) if (!(await run("revokeRole", { grantId: g.grantId }))) return;
  };

  return (
    <div className="max-w-5xl">
      <SettingsTitle
        title="People"
        action={
          <div className="flex items-center gap-2 text-sm">
            <span className="text-muted">Owner:</span>
            {p.ownerId !== null && <PersonAvatar name={userOf(data, p.ownerId)?.name ?? `user ${p.ownerId}`} size={20} />}
            <SimpleSelect
              value={p.ownerId === null ? "" : String(p.ownerId)}
              placeholder="Nobody"
              options={[{ value: "", label: "Nobody" }, ...data.users.filter((u) => u.status !== "disabled").map((u) => ({ value: String(u.id), label: u.name }))]}
              onChange={(v) => void run("updateProject", { projectId: p.id, ownerId: v ? Number(v) : null })}
            />
          </div>
        }
      />
      <div className="mb-4 flex items-center gap-2">
        <Input icon={Search} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search" inputClassName="w-72" />
        <div className="flex-1" />
        <Button primary icon={Plus} onClick={() => setAdding(true)}>
          Add people...
        </Button>
      </div>

      <h2 className="mb-1 text-sm font-medium text-muted uppercase">Project Team</h2>
      <table className="w-full table-fixed">
        <thead>
          <tr>
            <Th className="w-[45%]">Name</Th>
            <Th>Roles</Th>
            <Th className="w-10" />
          </tr>
        </thead>
        <tbody>
          {team.filter((r) => match(rowText(r))).map((row) => {
            const key = granteeKey(row);
            const members = row.kind === "group" ? (data.groups.find((g) => g.id === row.id)?.members ?? []) : [];
            return (
              <Fragment key={key}>
                <tr>
                  <Td>
                    {row.kind === "group" ? (
                      <button type="button" className="flex items-center gap-2 text-left" onClick={() => setOpen((o) => ({ ...o, [key]: !o[key] }))}>
                        <ChevronRight size={14} className={`shrink-0 text-faint transition-transform ${open[key] ? "rotate-90" : ""}`} />
                        <span className="badge chip-6 shrink-0">
                          <Users size={13} />
                        </span>
                        <span>
                          {groupLabel(data, row.id)} <span className="text-muted">({members.length})</span>
                        </span>
                      </button>
                    ) : (
                      <span className="flex items-center gap-2">
                        <span className="w-[14px] shrink-0" />
                        <PersonAvatar name={userOf(data, row.id)?.name ?? `user ${row.id}`} />
                        <span className="min-w-0">
                          <span className="flex items-center gap-2">
                            <span className="truncate">{userOf(data, row.id)?.name ?? `user ${row.id}`}</span>
                            {p.ownerId === row.id && <span className="chip chip-4 shrink-0">project owner</span>}
                          </span>
                          <span className="block truncate text-xs text-muted">{userOf(data, row.id)?.email}</span>
                        </span>
                      </span>
                    )}
                  </Td>
                  <Td>
                    <RolePicker data={data} selected={row.roles.map((r) => r.roleId)} onChange={(next) => void setRoles(row, next)} />
                  </Td>
                  <Td>
                    <Menu
                      anchor={
                        <button type="button" className="icon-btn" title="More">
                          <MoreHorizontal size={16} />
                        </button>
                      }
                      items={[{ key: "remove", label: "Remove from project", onClick: () => void removeAll(row) }]}
                    />
                  </Td>
                </tr>
                {row.kind === "group" &&
                  open[key] &&
                  members.map((uid) => (
                    <tr key={`${key}-${uid}`}>
                      <Td className="pl-12">
                        <span className="flex items-center gap-2">
                          <PersonAvatar name={userOf(data, uid)?.name ?? `user ${uid}`} size={20} />
                          <span className="truncate">{userOf(data, uid)?.name ?? `user ${uid}`}</span>
                          <span className="truncate text-xs text-muted">{userOf(data, uid)?.email}</span>
                        </span>
                      </Td>
                      <Td className="text-xs text-muted">through {groupLabel(data, row.id)}</Td>
                      <Td />
                    </tr>
                  ))}
              </Fragment>
            );
          })}
          {team.length === 0 && (
            <tr>
              <Td className="text-faint">Nobody has a role in this project yet.</Td>
              <Td />
              <Td />
            </tr>
          )}
        </tbody>
      </table>

      <h2 className="mt-8 mb-1 text-sm font-medium text-muted uppercase">Other People with Access</h2>
      <p className="mb-1 text-xs text-faint">
        Reaching this project through a global grant or the administrator flag. Change them under <Link to="/admin/roles" className="text-value hover:underline">Roles</Link>.
      </p>
      <table className="w-full table-fixed">
        <thead>
          <tr>
            <Th className="w-[45%]">Name</Th>
            <Th>Roles</Th>
            <Th className="w-10" />
          </tr>
        </thead>
        <tbody>
          {global.filter((r) => match(rowText(r))).map((row) => (
            <tr key={`x-${granteeKey(row)}`}>
              <Td>
                <span className="flex items-center gap-2">
                  {row.kind === "group" ? (
                    <span className="badge chip-6 shrink-0">
                      <Users size={13} />
                    </span>
                  ) : (
                    <PersonAvatar name={userOf(data, row.id)?.name ?? `user ${row.id}`} />
                  )}
                  <span className="min-w-0">
                    <span className="block truncate">{row.kind === "group" ? groupLabel(data, row.id) : (userOf(data, row.id)?.name ?? `user ${row.id}`)}</span>
                    <span className="block truncate text-xs text-muted">{row.kind === "group" ? "group, all projects" : userOf(data, row.id)?.email}</span>
                  </span>
                </span>
              </Td>
              <Td className="text-muted">{row.roles.map((r) => roleLabel(data, r.roleId)).join(", ")}</Td>
              <Td />
            </tr>
          ))}
          {admins.filter((u) => match(`${u.name} ${u.email}`)).map((u) => (
            <tr key={`a-${u.id}`}>
              <Td>
                <span className="flex items-center gap-2">
                  <PersonAvatar name={u.name} />
                  <span className="min-w-0">
                    <span className="block truncate">{u.name}</span>
                    <span className="block truncate text-xs text-muted">{u.email}</span>
                  </span>
                </span>
              </Td>
              <Td className="text-muted">Administrator</Td>
              <Td />
            </tr>
          ))}
        </tbody>
      </table>

      {adding && <AddPeopleDialog p={p} data={data} reload={reload} onClose={() => setAdding(false)} />}
    </div>
  );
}

// ----------------------------------------------------------------- grants grouped by grantee

interface Grantee {
  kind: "user" | "group";
  id: number;
}
interface Row extends Grantee {
  roles: { roleId: number; grantId: number }[];
}
const granteeKey = (g: Grantee): string => `${g.kind}:${g.id}`;
const granteeInput = (g: Grantee): { userId: number } | { groupId: number } => (g.kind === "group" ? { groupId: g.id } : { userId: g.id });

/** One row per user / group, groups first, each with every role granted to it in the scope. */
function groupGrants(grants: AdminOverview["grants"]): Row[] {
  const rows = new Map<string, Row>();
  for (const g of grants) {
    const who: Grantee = g.groupId !== null ? { kind: "group", id: g.groupId } : { kind: "user", id: g.userId! };
    const key = granteeKey(who);
    const row = rows.get(key) ?? { ...who, roles: [] };
    row.roles.push({ roleId: g.roleId, grantId: g.id });
    rows.set(key, row);
  }
  return [...rows.values()].sort((a, b) => (a.kind === b.kind ? a.id - b.id : a.kind === "group" ? -1 : 1));
}

/** The Roles cell: a filterable checkbox list, shown as "Project Admin, Contributor". */
function RolePicker({ data, selected, onChange }: { data: AdminOverview; selected: number[]; onChange: (ids: number[]) => void }) {
  const items: SelectItem[] = data.roles.map((r) => ({ key: r.id, label: r.name, ...(r.description ? { description: r.description } : {}) }));
  const chosen = items.filter((i) => selected.includes(Number(i.key)));
  return (
    <Select
      multiple
      filter={{ placeholder: "Filter roles" }}
      type={SelectType.INLINE}
      data={items}
      selected={chosen}
      label="No roles"
      selectedLabel={chosen.length ? chosen.map((i) => String(i.label)).join(", ") : "No roles"}
      onChange={(list: SelectItem[]) => onChange(list.map((i) => Number(i.key)))}
    />
  );
}

// ----------------------------------------------------------------- Add People

interface Entry {
  key: string;
  /** An existing account, a group, or an email address nobody has an account for yet. */
  kind: "user" | "group" | "email";
  id?: number;
  email?: string;
  name: string;
  roles: number[];
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function AddPeopleDialog({ p, data, reload, onClose }: { p: AdminProject; data: AdminOverview; reload: () => Promise<void>; onClose: () => void }) {
  const defaultRole = data.roles.find((r) => r.name === "Developer")?.id ?? data.roles[0]?.id ?? 0;
  const [entries, setEntries] = useState<Entry[]>([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const chosen = new Set(entries.map((e) => e.key));
  const needle = q.trim().toLowerCase();
  const suggestions: Entry[] = [
    ...data.groups.filter((g) => g.name.toLowerCase().includes(needle)).map((g): Entry => ({ key: `group:${g.id}`, kind: "group", id: g.id, name: g.name, roles: [defaultRole] })),
    ...data.users
      .filter((u) => u.status !== "disabled" && (u.name.toLowerCase().includes(needle) || u.email.toLowerCase().includes(needle)))
      .map((u): Entry => ({ key: `user:${u.id}`, kind: "user", id: u.id, name: u.name, email: u.email, roles: [defaultRole] })),
  ]
    .filter((e) => !chosen.has(e.key))
    .slice(0, 8);
  const inviteable = EMAIL.test(q.trim()) && !chosen.has(`email:${q.trim().toLowerCase()}`) && !data.users.some((u) => u.email.toLowerCase() === q.trim().toLowerCase());

  const add = (e: Entry): void => {
    setEntries((list) => [...list, e]);
    setQ("");
  };

  const submit = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      for (const e of entries) {
        let userId = e.kind === "user" ? e.id! : null;
        if (e.kind === "email") {
          const r = await client.http.command<{ userId: number }>("inviteUser", { email: e.email, name: e.email!.split("@")[0] }, uuid());
          userId = r.userId;
        }
        for (const role of e.roles) {
          await client.http.command("grantRole", { projectId: p.id, ...(e.kind === "group" ? { groupId: e.id } : { userId }), role }, uuid());
        }
      }
      await reload();
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : (err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog show label="Add People" onCloseAttempt={onClose} showCloseButton trapFocus autoFocusFirst={false} contentClassName="!w-[42rem] !max-w-[calc(100vw-2rem)]">
      <DialogHeader>Add People</DialogHeader>
      <DialogContent>
        <p className="mb-3 text-sm text-muted">You can add users who already have accounts or invite new users by email</p>
        <div className="relative">
          <Input size={InputSize.FULL} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Type a name, a group or an email address" autoFocus />
          {q.trim() && (suggestions.length > 0 || inviteable) && (
            <ul className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded border border-line bg-surface py-1 shadow-lg">
              {suggestions.map((s) => (
                <li key={s.key}>
                  <button type="button" className="flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-sunken" onClick={() => add(s)}>
                    {s.kind === "group" ? (
                      <span className="badge chip-6">
                        <Users size={13} />
                      </span>
                    ) : (
                      <PersonAvatar name={s.name} size={20} />
                    )}
                    <span>{s.name}</span>
                    <span className="text-xs text-muted">{s.kind === "group" ? "group" : s.email}</span>
                  </button>
                </li>
              ))}
              {inviteable && (
                <li>
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-sunken"
                    onClick={() => add({ key: `email:${q.trim().toLowerCase()}`, kind: "email", email: q.trim(), name: q.trim(), roles: [defaultRole] })}
                  >
                    <Plus size={14} className="text-faint" />
                    Invite {q.trim()}
                  </button>
                </li>
              )}
            </ul>
          )}
        </div>

        <table className="mt-4 w-full table-fixed">
          <thead>
            <tr>
              <Th className="w-1/2">Name</Th>
              <Th>Roles</Th>
              <Th className="w-10" />
            </tr>
          </thead>
          <tbody>
            {entries.map((e) => (
              <tr key={e.key}>
                <Td>
                  <span className="flex items-center gap-2">
                    {e.kind === "group" ? (
                      <span className="badge chip-6 shrink-0">
                        <Users size={13} />
                      </span>
                    ) : (
                      <PersonAvatar name={e.name} size={20} />
                    )}
                    <span className="min-w-0">
                      <span className="block truncate">{e.name}</span>
                      <span className="block truncate text-xs text-muted">{e.kind === "email" ? "will be invited by email" : e.kind === "group" ? "group" : e.email}</span>
                    </span>
                  </span>
                </Td>
                <Td>
                  <RolePicker data={data} selected={e.roles} onChange={(roles) => setEntries((list) => list.map((x) => (x.key === e.key ? { ...x, roles } : x)))} />
                </Td>
                <Td>
                  <Button icon={X} title="Remove" onClick={() => setEntries((list) => list.filter((x) => x.key !== e.key))} />
                </Td>
              </tr>
            ))}
            {entries.length === 0 && (
              <tr>
                <Td className="text-faint">Nobody selected yet.</Td>
                <Td />
                <Td />
              </tr>
            )}
          </tbody>
        </table>
        {error && <div className="mt-3 rounded bg-danger-soft px-2 py-1 text-danger">{error}</div>}
      </DialogContent>
      <Panel>
        <Button primary loader={busy} disabled={busy || entries.length === 0 || entries.some((e) => e.roles.length === 0)} onClick={() => void submit()}>
          Invite
        </Button>
        <Button onClick={onClose}>Cancel</Button>
      </Panel>
    </Dialog>
  );
}
